import type React from 'react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Loader2, Mic } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button, buttonVariants } from '@/components/ui/button';
import { BranchRequiredState } from '@/components/common/BranchRequiredState';
import { useT } from '@/contexts/I18nContext';
import { useCan } from '@/hooks/useCan';
import { useCurrentZone } from '@/hooks/useCurrentZone';
import { useZoneFeatures } from '@/hooks/useZoneFeatures';
import type { FeatureKey } from '@/api/features';
import { extractApiError } from '@/utils/apiError';
import { cn } from '@/lib/utils';

/** M1.3 (Alexey 11.09 / 24.08): a function's settings page shows NOTHING until the
 *  function is switched on for the branch — only «Включить <Функция> для этого
 *  филиала». Switching on pins the tab in the sidebar and blinks it (M1.6). */
export function FeatureGate({
  feature,
  path,
  children,
}: {
  feature: FeatureKey;
  path: string;
  children: React.ReactNode;
}) {
  const { t } = useT();
  const { zoneId } = useCurrentZone();
  const { search } = useLocation();
  const canEdit = useCan('zone.config.edit', zoneId);
  const { features, isLoading, enable } = useZoneFeatures(zoneId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!zoneId) return <BranchRequiredState />;
  if (isLoading && !features) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
      </div>
    );
  }
  const state = features?.[feature];
  if (state?.enabled) return <>{children}</>;

  const name = t(`features.${feature}.name`);
  if (state?.requirement === 'microphone') {
    return (
      <Card variant="section">
        <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
          <Mic className="size-8 text-muted-foreground" />
          <p className="max-w-md text-sm text-muted-foreground">{t('features.needsMicrophone')}</p>
          <Link to={{ pathname: '/management/equipment', search }} className={cn(buttonVariants({ variant: 'outline', size: 'sm' }))}>
            {t('features.goToEquipment')}
          </Link>
        </CardContent>
      </Card>
    );
  }

  async function turnOn() {
    setBusy(true);
    setError(null);
    try {
      await enable(feature, true, path);
    } catch (e) {
      setError(extractApiError(e, t('error.general')));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card variant="section">
      <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
        <p className="max-w-md text-sm text-muted-foreground">{t(`features.${feature}.pitch`)}</p>
        {canEdit ? (
          <Button onClick={turnOn} disabled={busy}>
            {busy && <Loader2 className="size-4 animate-spin" />}
            {t('features.enableFor', { name })}
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground">{t('features.askAdmin')}</p>
        )}
        {error && <p className="text-xs text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}

/** Small «Выключить» affordance at the bottom of an enabled function's page. */
export function FeatureDisableLink({ feature }: { feature: FeatureKey }) {
  const { t } = useT();
  const { zoneId } = useCurrentZone();
  const canEdit = useCan('zone.config.edit', zoneId);
  const { enable } = useZoneFeatures(zoneId);
  const [confirm, setConfirm] = useState(false);
  if (!canEdit) return null;
  const name = t(`features.${feature}.name`);
  return (
    <div className="pt-2 text-right text-xs text-muted-foreground">
      {confirm ? (
        <span className="inline-flex items-center gap-2">
          {t('features.disableConfirm', { name })}
          <Button size="sm" variant="destructive" onClick={() => void enable(feature, false)}>{t('features.disable')}</Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirm(false)}>{t('common.cancel')}</Button>
        </span>
      ) : (
        <button type="button" className="underline-offset-4 hover:underline" onClick={() => setConfirm(true)}>
          {t('features.disableFor', { name })}
        </button>
      )}
    </div>
  );
}

/** Анализ речи — placeholder page (N1: «можно заложить страницу, но пока пустую»). */
export function SpeechSettingsPlaceholder() {
  const { t } = useT();
  return (
    <FeatureGate feature="speech" path="/management/speech">
      <Card variant="section">
        <CardContent className="py-8 text-sm text-muted-foreground">{t('features.speech.placeholder')}</CardContent>
      </Card>
    </FeatureGate>
  );
}
